import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { FieldProps, getIn } from 'formik';
import DomPurify from 'dompurify';
import { CircularProgress, InputAdornment, Slide } from '@mui/material';
import { clsx } from 'clsx';
import { getDataIdGenerator } from '../../shared/utils/dataId';
import { TextField, TextFieldProps } from '../TextField/TextField';
import styles from './FormikAsyncValidationTextField.module.scss';
import { Icon } from '../../icon/Icon';
import { AsyncValidationStatus } from '../../shared/components/AsyncValidationForm/interfaces/async-validation-form';
import { useAsyncValidationForm } from '../../shared/components/AsyncValidationForm/hooks/useAsyncValidationForm';

export type FormikAsyncValidatedTextFieldProps = TextFieldProps & FieldProps;

export function FormikAsyncValidatedTextField({
  field,
  form: { setFieldValue, errors, validateField, touched, setFieldTouched },
  label,
  placeholder,
  id,
  helperText,
  ...props
}: FormikAsyncValidatedTextFieldProps & FieldProps) {
  const [inputValue, setInputValue] = useState<string>(field.value || '');
  const containerRef = useRef(null);

  const isTouched = !!touched[field.name];

  const { fields, setFieldStatus, registerAsyncField } = useAsyncValidationForm();

  useEffect(() => {
    registerAsyncField(field.name as never);
  }, []);

  const onChangeHandler = async (e: ChangeEvent<HTMLInputElement>) => {
    setFieldStatus(field.name as never, AsyncValidationStatus.PENDING);
    setInputValue(e.target.value);

    if (props.onChange) {
      props.onChange(e);
    }

    if (!isTouched) {
      await setFieldTouched(field.name, true, false);
    }

    await setFieldValue(field.name, e.target.value, false);
    await validateField(field.name);
  };

  const asyncValidationStatus = getIn(fields, field.name) as AsyncValidationStatus;
  const errorMessage = getIn(errors, field.name);

  const error =
    asyncValidationStatus === AsyncValidationStatus.ERRORED ||
    asyncValidationStatus === AsyncValidationStatus.INVALID ||
    !!errorMessage;
  const loading = asyncValidationStatus === AsyncValidationStatus.IN_PROGRESS;
  const asyncValidationSuccess = asyncValidationStatus === AsyncValidationStatus.VALID;
  const showError = error && errorMessage && isTouched && !loading;

  let endAdornment = null;

  if (loading) {
    endAdornment = (
      <InputAdornment position='end'>
        <CircularProgress size={10} className={styles.progress} />
      </InputAdornment>
    );
  }

  if (asyncValidationSuccess && !showError) {
    endAdornment = (
      <InputAdornment position='end'>
        <Icon name='checked-tick' size={3} className='text-success' dataId='validation-success' />
      </InputAdornment>
    );
  }

  const dataIdGenerator = getDataIdGenerator(field.name, 'textFieldValidator');

  return (
    <>
      <TextField
        {...field}
        {...props}
        id={id || field.name}
        dataId={dataIdGenerator('')}
        label={label}
        color={!showError && asyncValidationSuccess ? 'success' : undefined}
        placeholder={placeholder || String(label) || ''}
        value={inputValue}
        onChange={onChangeHandler}
        error={showError}
        autoComplete='off'
        className={clsx(props.className, styles.field)}
        InputProps={{
          ...props.InputProps,
          endAdornment
        }}
      />

      <div ref={containerRef}>
        {showError ? (
          <Slide
            direction='down'
            in
            timeout={300}
            unmountOnExit
            container={containerRef.current}
            data-id={dataIdGenerator('fieldError')}
          >
            <span className={clsx('mt-1', styles.fieldError)}>
              <span dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(errorMessage) }} />
            </span>
          </Slide>
        ) : null}

        {!error && helperText ? (
          <Slide
            direction='down'
            in
            timeout={300}
            container={containerRef.current}
            appear={!!containerRef.current}
            data-id={dataIdGenerator('helperText')}
          >
            <span className={styles.fieldHelp}>{helperText}</span>
          </Slide>
        ) : null}
      </div>
    </>
  );
}
